import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { getErrorMessage } from '@/lib/apiErrors';
import { Loader2 } from 'lucide-react';
import {
  useAssignableUsers,
  useMemberOpenTicketCount,
  useRemoveUserFromOrganization,
  useUpdateUserOrganization
} from '../hooks/useUsers';
import type { UserDto } from '../types/users';
import { describeHandoff, toOpenTicketHandoff, type HandoffChoice } from '../lib/memberHandoff';

interface OpenTicketHandoffFieldProps {
  userId: string;
  organizationId?: string;
  openTicketCount: number;
  value: HandoffChoice;
  onChange: (value: HandoffChoice) => void;
}

/**
 * A departing member's tickets that aren't closed need a new owner - an active, assignable member other than them -
 * or must be left unassigned on purpose. The server refuses the change until one is chosen.
 */
export const OpenTicketHandoffField: React.FC<OpenTicketHandoffFieldProps> = ({
  userId,
  organizationId,
  openTicketCount,
  value,
  onChange
}) => {
  const { data: assignableUsers = [] } = useAssignableUsers(organizationId);
  const candidates = assignableUsers.filter((u) => u.id !== userId);

  if (openTicketCount === 0) return null;

  return (
    <div className="space-y-2 p-3 border rounded-lg">
      <Label htmlFor="handoff">
        {openTicketCount === 1
          ? 'They are assigned to 1 ticket that isn\'t closed. Who should take it over?'
          : `They are assigned to ${openTicketCount} tickets that aren't closed. Who should take them over?`}
      </Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id="handoff" className="w-full">
          <SelectValue placeholder="Choose who takes over" />
        </SelectTrigger>
        <SelectContent>
          {candidates.map((u) => (
            <SelectItem key={u.id} value={u.id}>
              {u.fullName}
            </SelectItem>
          ))}
          <SelectItem value="unassign">Leave them unassigned</SelectItem>
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">
        Each ticket gets a history entry, and the new assignee is notified.
      </p>
    </div>
  );
};

interface MemberOffboardDialogProps {
  user: UserDto | null;
  organizationId?: string;
  onOpenChange: (open: boolean) => void;
}

/**
 * Takes a member out of an organization. Deactivating is the default: it's reversible and keeps their follows.
 * Removing deletes the membership and their follows. Either way their tickets that aren't closed are handed off.
 */
export const MemberOffboardDialog: React.FC<MemberOffboardDialogProps> = ({ user, organizationId, onOpenChange }) => {
  const membership = user?.organizations?.find((org) => org.organizationId === organizationId);
  const isActiveMember = membership?.isActive ?? true;

  const [action, setAction] = useState<'deactivate' | 'remove'>('deactivate');
  const [handoff, setHandoff] = useState<HandoffChoice>('');

  const { data: openTicketCount = 0, isLoading: isCountLoading } =
    useMemberOpenTicketCount(user?.id, organizationId, !!user);
  const { data: assignableUsers = [] } = useAssignableUsers(organizationId);
  const updateUserOrgMutation = useUpdateUserOrganization(organizationId);
  const removeUserMutation = useRemoveUserFromOrganization(organizationId);

  // An inactive member can only be removed
  const effectiveAction = isActiveMember ? action : 'remove';
  const needsHandoff = openTicketCount > 0 && !handoff;
  const isPending = updateUserOrgMutation.isPending || removeUserMutation.isPending;

  const close = () => {
    setAction('deactivate');
    setHandoff('');
    onOpenChange(false);
  };

  const handleConfirm = async () => {
    if (!user) return;
    const handoffRequest = openTicketCount > 0 ? toOpenTicketHandoff(handoff) : {};
    const assigneeName = assignableUsers.find((u) => u.id === handoff)?.fullName;

    try {
      if (effectiveAction === 'deactivate') {
        const result = await updateUserOrgMutation.mutateAsync({
          userId: user.id,
          orgData: {
            role: membership?.role ?? 'Agent',
            isActive: false,
            isAssignable: membership?.isAssignable ?? true,
            ...handoffRequest
          }
        });
        toast({
          title: 'User deactivated',
          description: `${user.fullName} can no longer access this organization.` +
            describeHandoff(result.handedOffTicketCount, handoff, assigneeName),
        });
      } else {
        const result = await removeUserMutation.mutateAsync({ userId: user.id, handoff: handoffRequest });
        toast({
          title: 'User removed',
          description: `${user.fullName} has been removed from the organization.` +
            describeHandoff(result.handedOffTicketCount, handoff, assigneeName),
        });
      }
      close();
    } catch (err) {
      toast({
        title: 'Error',
        description: getErrorMessage(err, effectiveAction === 'deactivate' ? 'Failed to deactivate user' : 'Failed to remove user'),
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={(open) => { if (!open) close(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isActiveMember ? `Deactivate or remove ${user?.fullName ?? ''}` : `Remove ${user?.fullName ?? ''}`}
          </DialogTitle>
          <DialogDescription>
            {isActiveMember
              ? 'Choose how to take away their access to this organization.'
              : 'They are already deactivated. Removing them deletes their membership.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {isActiveMember && (
            <div className="space-y-2">
              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                <input
                  type="radio"
                  name="offboardAction"
                  value="deactivate"
                  checked={action === 'deactivate'}
                  onChange={() => setAction('deactivate')}
                  className="h-4 w-4 mt-1"
                />
                <div>
                  <p className="font-medium">Deactivate (recommended)</p>
                  <p className="text-sm text-muted-foreground">
                    They can't access this organization until you reactivate them. Their ticket follows are kept.
                  </p>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                <input
                  type="radio"
                  name="offboardAction"
                  value="remove"
                  checked={action === 'remove'}
                  onChange={() => setAction('remove')}
                  className="h-4 w-4 mt-1"
                />
                <div>
                  <p className="font-medium">Remove from organization</p>
                  <p className="text-sm text-muted-foreground">
                    Their membership and ticket follows are deleted. Add them again to give back access.
                  </p>
                </div>
              </label>
            </div>
          )}

          {user && (
            isCountLoading ? (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Checking their tickets...
              </p>
            ) : (
              <OpenTicketHandoffField
                userId={user.id}
                organizationId={organizationId}
                openTicketCount={openTicketCount}
                value={handoff}
                onChange={setHandoff}
              />
            )
          )}

          <p className="text-sm text-muted-foreground">
            Their name stays on tickets, comments and history. Their user account stays active if they belong to other
            organizations.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isPending || isCountLoading || needsHandoff}
            variant={effectiveAction === 'remove' ? 'destructive' : 'default'}
          >
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {effectiveAction === 'remove' ? 'Removing...' : 'Deactivating...'}
              </>
            ) : effectiveAction === 'remove' ? 'Remove' : 'Deactivate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
